import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { errorDetail } from '@/lib/api-errors';
import {
  iconArrowRight,
  iconCircleCheck,
  iconCircleExclamation,
  iconClock,
  iconCoins,
  iconSpinnerThird,
  iconStar,
  iconStarSolid,
  iconXmark,
} from '@/lib/icons';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { cn } from '@/lib/utils';
import {
  useGetMyG2ReviewCreditQuery,
  useSubmitG2ReviewCreditMutation,
  type G2ReviewCreditRequest,
} from '@/store/features/g2-review-credits.feature';
import { useUser } from '@clerk/react';
import {
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
  usePostHog,
} from 'posthog-js/react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

const G2_REVIEW_FLAG = 'g2_review_campaign_v1';

/** The reward, kept in sync with G2_REVIEW_CREDITS on the backend
 *  (cleanse_api/core/config.py). Display-only: the server grants it when staff
 *  approve the claim in Management → G2 Credits. */
const CREDITS = (500).toLocaleString();

type Campaign = { url: string; image: string | null };

/** An https link; with `g2Only`, only on g2.com. */
function httpsUrl(raw: unknown, g2Only: boolean): string | null {
  if (typeof raw !== 'string') return null;
  try {
    const u = new URL(raw.trim());
    const host = u.hostname.toLowerCase();
    const isG2 = host === 'g2.com' || host.endsWith('.g2.com');
    return u.protocol === 'https:' && (isG2 || !g2Only) ? u.href : null;
  } catch {
    return null;
  }
}

/**
 * The campaign from the flag's payload — the G2 link as a bare URL, or
 * `{ "url": "…", "image": "…" }` with an optional picture (a GIF works) for
 * the dialog — so both can change in PostHog without a deploy. Only an https
 * g2.com link counts; without one there is no bar.
 */
function readCampaign(payload: unknown): Campaign | null {
  let data = payload;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      // A bare URL, not JSON.
    }
  }
  const obj = (typeof data === 'string' ? { url: data } : (data ?? {})) as {
    url?: unknown;
    image?: unknown;
  };
  const url = httpsUrl(obj.url, true);
  return url ? { url, image: httpsUrl(obj.image, false) } : null;
}

/** Loose client-side check mirroring is_valid_g2_review_url on the BE: a
 *  published Cleanlist AI review, e.g.
 *  https://www.g2.com/products/cleanlist-ai/reviews/cleanlist-ai-review-12345678
 *  (G2 also serves it under a language prefix such as /de/). The server is
 *  authoritative; this only gives instant feedback. */
const G2_REVIEW_PATH =
  /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/cleanlist-ai\/reviews\/[a-z0-9-]+-review-\d+\/?$/;

function looksLikeG2ReviewUrl(url: string): boolean {
  try {
    const u = new URL(url.trim());
    const host = u.hostname.toLowerCase();
    return (
      u.protocol === 'https:' &&
      (host === 'g2.com' || host === 'www.g2.com') &&
      G2_REVIEW_PATH.test(u.pathname.toLowerCase())
    );
  } catch {
    return false;
  }
}

/**
 * Per-user progress. `invite` offers the reward; after opening G2 the bar
 * becomes `claim`, a reminder to send the link once G2 publishes the review.
 * Closing the invite hides it for 7 days; closing the reminder, for good.
 */
type Stage = 'hidden' | 'invite' | 'claim';
const STAGE_KEY_PREFIX = 'cl_g2_review_dismissed_';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const OPENED = 'opened';
const DONE = 'done';

function readStage(userId: string): Stage {
  try {
    const raw = localStorage.getItem(`${STAGE_KEY_PREFIX}${userId}`);
    if (raw === DONE) return 'hidden';
    if (raw === OPENED) return 'claim';
    if (raw && Date.now() - Number(raw) < DISMISS_TTL_MS) return 'hidden';
    return 'invite';
  } catch {
    return 'invite';
  }
}

function writeStage(userId: string, value: string) {
  try {
    localStorage.setItem(`${STAGE_KEY_PREFIX}${userId}`, value);
  } catch {
    // Storage unavailable (private mode) — progress just won't persist.
  }
}

/** Support chat, with the message started: the fallback for a screenshot. */
function openChat() {
  window.Intercom?.('showNewMessage', 'Here is my G2 review: ');
}

/**
 * Full-width bar across the top of the window, above the nav and the page,
 * inviting users to review Cleanlist AI
 * on G2 for credits; its button opens the reward dialog, where they paste the
 * published review's link for staff to approve (Management → G2 Credits).
 * Gated on the org-keyed PostHog flag `g2_review_campaign_v1` (the backend's
 * claim endpoints read the same key), whose release conditions decide who is
 * invited: target by usage only, never by satisfaction (G2's incentive
 * rules). Only paying and AppSumo orgs are invited, never Free or a trial
 * (the claim endpoint applies the same rule). Cleanlist staff never see it.
 * While a sent link waits for review the bar says thanks; once the credits
 * are granted it's gone.
 */
export function G2ReviewBanner() {
  const { user } = useUser();
  const userId = user?.id;
  const posthog = usePostHog();
  const enabled = useFeatureFlagEnabled(G2_REVIEW_FLAG) === true;
  const campaign = readCampaign(useFeatureFlagPayload(G2_REVIEW_FLAG));
  // Both read false until the entitlements load, so the bar never flashes.
  const { hasActivePaidStripe, isAppSumo, trial } = useFeatureAccess();
  const invited = (hasActivePaidStripe || isAppSumo) && trial?.active !== true;
  const [stage, setStage] = useState<Stage>('hidden');
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (userId) setStage(readStage(userId));
  }, [userId]);

  const isStaff = (user?.emailAddresses ?? []).some((e) =>
    e.emailAddress.toLowerCase().trim().endsWith('@cleanlist.ai')
  );
  const shown =
    enabled && !!campaign && invited && !isStaff && stage !== 'hidden';

  // The user's latest claim: one reward per person, from any workspace.
  const { data: myClaim, isLoading } = useGetMyG2ReviewCreditQuery(undefined, {
    skip: !shown,
  });

  if (!shown || !campaign || isLoading || myClaim?.status === 'approved') {
    return null;
  }

  const save = (value: string, next: Stage) => {
    if (userId) writeStage(userId, value);
    setStage(next);
  };

  const sendViaChat = () => {
    posthog?.capture('g2_review_chat_opened');
    openChat();
  };

  // A link waiting for review gets a thank-you; a rejected one brings the
  // reminder back (the dialog says why).
  const view: Stage | 'pending' =
    myClaim?.status === 'pending' ? 'pending' : myClaim ? 'claim' : stage;

  const claimCredits = () => {
    posthog?.capture('g2_review_dialog_opened');
    setDialogOpen(true);
  };

  const dismiss = () => {
    posthog?.capture('g2_review_banner_dismissed', { stage: view });
    save(view === 'invite' ? String(Date.now()) : DONE, 'hidden');
  };

  return (
    <>
      <div
        role='region'
        aria-label='G2 review reward'
        className={cn(
          'flex h-10 items-center gap-3 border-b px-4',
          'border-primary/20 bg-primary/5'
        )}
      >
        <AppIcon
          icon={iconStar}
          className='h-4 w-4 shrink-0 text-primary'
          style={{ fontSize: 14 }}
        />

        <p className='truncate text-[13px] text-foreground'>
          {view === 'pending' ? (
            <>
              <span className='font-semibold'>Thanks for your G2 review!</span>
              <span className='hidden sm:inline'>
                {' '}
                We're checking your link. Your {CREDITS} credits arrive once
                it's approved.
              </span>
            </>
          ) : view === 'claim' ? (
            <>
              <span className='font-semibold'>Left your G2 review?</span>
              <span className='hidden sm:inline'>
                {' '}
                Once G2 publishes it, send us the link to get your {
                  CREDITS
                }{' '}
                credits.
              </span>
            </>
          ) : (
            <>
              <span className='font-semibold'>
                Get {CREDITS} credits for an honest G2 review
              </span>
              <span className='hidden sm:inline'>, whatever your rating.</span>
            </>
          )}
        </p>

        <div className='ml-auto flex shrink-0 items-center gap-2'>
          {view !== 'pending' && (
            <button
              type='button'
              onClick={
                view === 'claim' ? () => setDialogOpen(true) : claimCredits
              }
              className='inline-flex h-7 items-center gap-1 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden'
            >
              {view === 'claim' ? 'Send link' : 'Claim credits'}
              <AppIcon icon={iconArrowRight} className='h-3 w-3' />
            </button>
          )}
          <button
            type='button'
            onClick={dismiss}
            aria-label='Dismiss G2 review banner'
            className='rounded-md p-1 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden'
          >
            <AppIcon icon={iconXmark} className='h-3.5 w-3.5' />
          </button>
        </div>
      </div>

      <G2ReviewDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        campaign={campaign}
        claim={myClaim ?? null}
        onReview={() => {
          posthog?.capture('g2_review_banner_clicked');
          save(OPENED, 'claim');
        }}
        onSubmitted={() => posthog?.capture('g2_review_link_submitted')}
        onChat={sendViaChat}
      />
    </>
  );
}

/**
 * The reward card the bar opens: what it's worth, the four steps (review on
 * G2, paste the link here, get the credits), the G2 button, and the link form
 * or the claim's status.
 */
export function G2ReviewDialog({
  open,
  onOpenChange,
  campaign,
  claim,
  onReview,
  onSubmitted,
  onChat,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
  /** The user's latest claim, if they sent a link. */
  claim: G2ReviewCreditRequest | null;
  /** Opening G2 (the link itself opens it in a new tab). */
  onReview: () => void;
  /** A link was accepted for review. */
  onSubmitted: () => void;
  onChat: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[calc(100dvh-2rem)] gap-5 overflow-y-auto sm:max-w-md sm:rounded-2xl'>
        <DialogHeader className='space-y-1 text-left'>
          <DialogTitle className='flex items-center gap-2 text-2xl font-bold tracking-tight text-primary'>
            <AppIcon icon={iconStarSolid} style={{ fontSize: 22 }} />
            Claim {CREDITS} credits
          </DialogTitle>
          <DialogDescription className='text-[15px]'>
            Get rewarded for sharing your experience.
          </DialogDescription>
        </DialogHeader>

        {campaign.image ? (
          <img
            src={campaign.image}
            alt=''
            className='aspect-video w-full rounded-xl border border-primary/20 object-cover'
          />
        ) : (
          <div
            aria-hidden
            className='flex aspect-video w-full flex-col items-center justify-center gap-1 rounded-xl border border-primary/20 bg-primary/10'
          >
            <AppIcon
              icon={iconCoins}
              className='text-primary'
              style={{ fontSize: 36 }}
            />
            <p className='text-5xl font-bold tracking-tight text-primary'>
              +{CREDITS}
            </p>
            <p className='text-xs font-semibold tracking-widest text-primary/70 uppercase'>
              credits
            </p>
          </div>
        )}

        <p className='text-[15px] font-medium'>
          We really appreciate it. Your review helps other teams find Cleanlist
          AI, and tells us what to fix next.
        </p>

        <ol className='space-y-2.5 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm'>
          {[
            'Click the button below to open our G2 review page.',
            'Write an honest review of your experience, good or bad.',
            'Once G2 publishes it, paste the link to your review below.',
            <>
              <span className='font-semibold text-primary'>Done!</span> We add{' '}
              {CREDITS} credits to your workspace, whatever your rating.
            </>,
          ].map((step, i) => (
            <li key={i} className='flex gap-2'>
              <span className='font-bold text-primary'>{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        <div className='space-y-2'>
          <a
            href={campaign.url}
            target='_blank'
            rel='noopener noreferrer'
            onClick={onReview}
            className='inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-[15px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden'
          >
            <AppIcon icon={iconStarSolid} style={{ fontSize: 15 }} />
            Review Cleanlist AI on G2
          </a>
          <p className='text-center text-xs text-muted-foreground'>
            This is an incentivized review. One reward per person.
          </p>
        </div>

        <div className='border-t border-primary/20 pt-4'>
          <G2ClaimSection
            claim={claim}
            onSubmitted={onSubmitted}
            onChat={onChat}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Where the user sends their review link, or sees where their claim is. */
function G2ClaimSection({
  claim,
  onSubmitted,
  onChat,
}: {
  claim: G2ReviewCreditRequest | null;
  onSubmitted: () => void;
  onChat: () => void;
}) {
  const inputId = useId();
  const [link, setLink] = useState('');
  const [submit, { isLoading }] = useSubmitG2ReviewCreditMutation();
  const trimmed = link.trim();
  const valid = looksLikeG2ReviewUrl(trimmed);

  if (claim?.status === 'approved') {
    return (
      <p className='flex items-start gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm font-medium text-emerald-700 dark:text-emerald-300'>
        <AppIcon icon={iconCircleCheck} className='mt-0.5' />
        <span>
          {claim.credits_awarded.toLocaleString()} credits added to your
          workspace. Thank you for your review!
        </span>
      </p>
    );
  }

  if (claim?.status === 'pending') {
    return (
      <p className='flex items-start gap-2 rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm'>
        <AppIcon icon={iconClock} className='mt-0.5 text-primary' />
        <span>
          Thanks! We're checking your review. Your {CREDITS} credits arrive once
          it's approved.
        </span>
      </p>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || isLoading) return;
    try {
      await submit({ review_url: trimmed }).unwrap();
      onSubmitted();
      setLink('');
      toast.success(
        `Thanks! We'll check your review and add your ${CREDITS} credits once it's approved.`
      );
    } catch (err) {
      toast.error(
        errorDetail(
          err,
          "We couldn't send your link. Try again, or send it in support chat."
        )
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-2'>
      {claim?.status === 'rejected' && (
        <p
          role='alert'
          className='flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive'
        >
          <AppIcon icon={iconCircleExclamation} className='mt-0.5' />
          <span>
            We couldn't approve your last link. Paste a new one below.
            {claim.rejection_reason && (
              <span className='mt-1 block text-xs'>
                {claim.rejection_reason}
              </span>
            )}
          </span>
        </p>
      )}
      <label htmlFor={inputId} className='block text-sm font-semibold'>
        Your G2 review link
      </label>
      <div className='flex gap-2'>
        <Input
          id={inputId}
          type='url'
          inputMode='url'
          autoComplete='off'
          placeholder='https://www.g2.com/products/cleanlist-ai/reviews/…'
          value={link}
          onChange={(e) => setLink(e.target.value)}
          className='h-10'
        />
        <Button
          type='submit'
          variant='brand'
          className='h-10 shrink-0'
          disabled={!valid || isLoading}
        >
          {isLoading ? (
            <AppIcon
              icon={iconSpinnerThird}
              className='animate-spin'
              style={{ fontSize: 14 }}
            />
          ) : (
            'Send link'
          )}
        </Button>
      </div>
      {trimmed.length > 0 && !valid && (
        <p className='text-xs text-destructive'>
          Paste the link to your published review. It starts with
          https://www.g2.com/products/cleanlist-ai/reviews/
        </p>
      )}
      <p className='text-xs text-muted-foreground'>
        Only have a screenshot? Send it in{' '}
        <button
          type='button'
          onClick={onChat}
          className='font-semibold text-primary underline underline-offset-2 hover:text-primary/80'
        >
          support chat
        </button>
        .
      </p>
    </form>
  );
}
